import { Button, DialogOverlay, DialogOverlayActions, Paragraph } from '@fs/zion-ui'
import PropTypes from 'prop-types'
import React from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Dialog component that warns users about unsaved changes before exiting.
 * Displays a confirmation dialog with options to go back or proceed with exiting.
 *
 * @param {Object} props - Component props
 * @param {Object} props.overlay - Overlay state object containing open/close state and handlers
 * @param {Function} props.overlay.close - Function to close the overlay
 * @param {boolean} props.overlay.isOpen - Whether the overlay is currently open
 * @param {Function} props.onExit - Callback function executed when user confirms exit despite unsaved changes
 * @param {Function} [props.onBack] - Optional callback function for "Go Back" button. Defaults to closing the overlay
 * @returns {React.ReactElement} The unsaved changes dialog overlay
 */
const UnsavedChangesDialog = ({ overlay, onExit, onBack }) => {
  const [t] = useTranslation()

  return (
    <DialogOverlay
      {...overlay}
      size="sm"
      title={t('unsavedChangesOverlay.title', 'Unsaved Changes')}
      closeLabel={t('unsavedChangesOverlay.closeButton', 'Close')}
      footer={
        <DialogOverlayActions>
          <Button onClick={onBack || overlay.close} color="gray" data-testid="unsaved-changes-dialog-go-back-button">
            {t('unsavedChangesOverlay.goBackButton', 'Go Back')}
          </Button>
          <Button emphasis="high" color="danger" onClick={onExit} data-testid="unsaved-changes-dialog-exit-button">
            {t('unsavedChangesOverlay.exitButton', 'Exit')}
          </Button>
        </DialogOverlayActions>
      }
    >
      <Paragraph size="sm">
        {t('unsavedChangesOverlay.description', 'You have unsaved changes that will be lost if you exit.')}
      </Paragraph>
    </DialogOverlay>
  )
}

UnsavedChangesDialog.propTypes = {
  overlay: PropTypes.shape({
    close: PropTypes.func.isRequired,
    isOpen: PropTypes.bool,
  }).isRequired,
  onExit: PropTypes.func.isRequired,
  onBack: PropTypes.func,
}

export default UnsavedChangesDialog
